import React, { useMemo } from 'react';
import { Box, Stack } from '@mui/material';
import { TextView, Button, ChartTooltip } from '@atoms';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts';
import { DateTime } from 'luxon';
import { useAISrtTokens } from '@api';
import { toast } from 'react-toastify';
import CustomizedYAxisTick from '../particles/CustomizedYAxisTick';
import CustomizedXAxisTick from '../particles/CustomizedXAxisTick';
import AutoAwesomeOutlinedIcon from '@mui/icons-material/AutoAwesomeOutlined';
import QuestionAnswerOutlinedIcon from '@mui/icons-material/QuestionAnswerOutlined';
import MoodOutlinedIcon from '@mui/icons-material/MoodOutlined';
import { CommonCard, SkeletonChart } from '@organisms';

interface callGetSrtTokenUsageProps {
  billingAccount?: BillingAccount;
  dates: { start: DateTime | null; end: DateTime | null };
  aiResponseCount: number;
  aiSentimentCount: number;
  aiEnquiresCount: number;
}

const AIChart: React.FC<callGetSrtTokenUsageProps> = ({
  billingAccount,
  dates,
  aiResponseCount,
  aiSentimentCount,
  aiEnquiresCount,
}: callGetSrtTokenUsageProps) => {
  const { data: responseSrt, isFetching: isLoading } = useAISrtTokens({
    from: dates.start?.toISO({
      suppressMilliseconds: true,
      includeOffset: false
    }),
    to: dates.end?.toISO({
      suppressMilliseconds: true,
      includeOffset: false
    }),
    billingAccountId: billingAccount?.id
  });

  const values = useMemo(() => {
    if (!responseSrt?.data?.data || !dates.start || !dates.end) return [];

    const counts = responseSrt.data.data;
    const dateMap = new Map<string, AISrtCount>();
    counts.forEach((item) => {
      if (item.date) {
        dateMap.set(item.date.substring(0, 10), item);
      }
    });

    const newValues: AISrtCount[] = [];
    let day = dates.start;
    const lastDay = dates.end;
    while (day <= lastDay) {
      const key = day.toFormat('yyyy-MM-dd');
      newValues.push(
        dateMap.get(key) || {
          date: day.toISO(),
          aiResponseCount: 0,
          aiEnquiresCount: 0,
          aiSentimentCount: 0
        }
      );
      day = day.plus({ day: 1 });
    }
    return newValues;
  }, [responseSrt, dates.start, dates.end]);

  const downloadExport = async () => {
    const token = localStorage.getItem('token');
    const apiBase = process.env.REACT_APP_API_BASE;

    const from = dates.start?.toISO({
      suppressMilliseconds: true,
      includeOffset: false
    });
    const to = dates.end?.toISO({
      suppressMilliseconds: true,
      includeOffset: false
    });


    const query = new URLSearchParams()
    query.append('markupType', 'ai_processing');
    if (from) {
      query.append('startDate', from);
    }
    if (to) {
      query.append('endDate', to);
    }
    if (billingAccount?.id) {
      query.append('billingAccountId', billingAccount.id);
    }

    const urlApi = `${apiBase}/srt/csv-download?${query.toString()}`;

    const zone: string[] = DateTime.now().toFormat('ZZ').split(':');
    const hour = Number(zone[0]);
    const mint = Number(zone[1]);
    const totalMint: number = hour * 60 + mint;

    const fetchOptions: RequestInit = {
      headers: {
        Authorization: token ? `Bearer ${token}` : '',
        'Content-Type': 'application/json',
        timezone: `${totalMint}`
      },
      method: 'GET'
    };

    const response: Response = await fetch(urlApi, fetchOptions);

    if (response.status == 200) {
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute(
        'download',
        `AI Processing ${DateTime.now().toFormat('LLL dd yyyy, hh:mm:ss')}.csv`
      );
      document.body.appendChild(link);
      link.click();
    } else {
      toast('Something went wrong');
    }
  };

  return (
    <Box
      sx={{
        mt: 2
      }}>
      <Stack direction={'row'} justifyContent={'space-between'}>
        <TextView $fontSize={26} $fontWeight={700}>
          AI Processing
        </TextView>
        <Button
          label="Export"
          onClick={downloadExport}
          size="small"
          sx={{ mr: 2 }}
          disabled={isLoading}
        />
      </Stack>

      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: { xs: '1fr', sm: 'repeat(3, 1fr)' },
          gap: 2,
          gridAutoRows: '1fr',
          mt: 2,
          mb: 2
        }}
      >
        <CommonCard
          title="Responses"
          count={aiResponseCount}
          icon={<QuestionAnswerOutlinedIcon />}
          accent="#086BDE"
        />
        <CommonCard
          title="Enquiries"
          count={aiEnquiresCount}
          icon={<AutoAwesomeOutlinedIcon />}
          accent="#F5CB5C"
        />
        <CommonCard
          title="Sentiment"
          count={aiSentimentCount}
          icon={<MoodOutlinedIcon />}
          accent="#fe4a49"
        />
      </Box>
      {isLoading && !responseSrt?.data ? (
        <SkeletonChart height={300} />
      ) : (
      <Box
        mt={1}
        sx={{
          backgroundColor: 'rgba(52, 130, 207, 0.06)',
          border: '1px solid rgba(52, 130, 207, 0.15)',
          pt: 2,
          borderRadius: 2,
          height: 300
        }}>
        <ResponsiveContainer width="100%" height="100%">
          <BarChart data={values}>
            <CartesianGrid strokeDasharray="3 3" />
            <XAxis
              dataKey="date"
              interval={0}
              height={80}
              tick={(props) => CustomizedXAxisTick(props)}
            />
            <YAxis tick={(props) => CustomizedYAxisTick(props)} />
            <Tooltip
              itemSorter={(item) => {
                switch (item.dataKey) {
                  case 'aiResponseCount':
                    return 0;
                  case 'aiEnquiresCount':
                    return 1;
                  case 'aiSentimentCount':
                    return 1;
                  default:
                    return 2;
                }
              }}
              content={
                <ChartTooltip
                  nameMap={{
                    aiResponseCount: 'AI Response',
                    aiEnquiresCount: 'AI Enquiries',
                    aiSentimentCount: 'AI Sentiment'
                  }}
                  showTotal
                  filterZero
                />
              }
              cursor={{ fill: 'rgba(43, 34, 88, 0.06)' }}
            />
            <Bar dataKey="aiResponseCount" stackId="a" fill="#086BDE" />
            <Bar dataKey="aiEnquiresCount" stackId="a" fill="#F5CB5C" />
            <Bar dataKey="aiSentimentCount" stackId="a" fill="#fe4a49" />
          </BarChart>
        </ResponsiveContainer>
      </Box>
      )}
    </Box>
  );
};

export default AIChart;
